import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Column,
  ColumnDef,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Loader } from 'lucide-react';
import { useSearchParams } from 'react-router';

import { AgreementDTO, InsuranceAreaDTO } from '@/types/api-types';
import {
  BreadcrumbItemType,
  NormalizedAgreement,
} from '@/types/file-explorer-types';

import { cn } from '@/lib/utils';

import { useIsMobile } from '@/hooks/use-mobile';

import { useApiManagerStore } from '@/stores/apiManagerStore';
import { useFileExplorerStore } from '@/stores/fileExplorerStore';
import { useFiltersStore } from '@/stores/filtersStore';
import { useGlobalStore } from '@/stores/globalStore';

import { handleExportExcel } from '@/utils/export-excel';

import {
  ExplorerItemType,
  getColumnsConfig,
} from '@/constants/table-columns-config';

import { DetailsPanel } from '../DetailsPanel/DetailsPanel';
import TableRowSkeleton from '../TableRowSkeleton';
import { Checkbox } from '../ui/checkbox';
import { CheckboxSome } from '../ui/checkbox-some';
import AgreementSelectionBar from './AgreementSelectionBar';
import BreadCrumbsComponent from './BreadCrumbsComponent';
import TableActionsComponent from './TableActionsComponent';
import {
  ColumnHeaderAgreement,
  getActionsColumn,
  getSmallColumns,
} from './TableColumns';

declare module '@tanstack/table-core' {
  interface FilterFns {
    exact: FilterFn<any>;
  }
}

interface AgreementViewProps {
  breadcrumbs: BreadcrumbItemType[];
  entityType: ExplorerItemType;
  setCurrentUUID: (uuid: string) => void;
  setCurrentEntityType: (type: ExplorerItemType) => void;
  rowsPerPage: number;
  onNavigate?: (params: { uuid: string; entityType: ExplorerItemType }) => void;
  isSmall?: boolean;
  isMoveFile?: boolean;
  searchTerm?: string;
  isLoading?: boolean;
  refetchData?: (pagination?: { pageIndex: number; pageSize: number }) => void;
  parentUuid?: InsuranceAreaDTO['uuid'];
}

export const AgreementView: FC<AgreementViewProps> = ({
  breadcrumbs,
  entityType,
  setCurrentUUID,
  setCurrentEntityType,
  onNavigate,
  isSmall = false,
  isMoveFile = false,
  searchTerm,
  rowsPerPage,
  parentUuid,
}) => {
  const [searchParams] = useSearchParams();

  const { updateAgreement, updateFileAfterMove, totalActiveAgreements } =
    useApiManagerStore();
  const { fileUuidToMove, setFileUuidToMove, setIsMoveFileDialogOpen } =
    useGlobalStore();
  const {
    sorting,
    setSorting,
    visibleColumns,
    setVisibleColumns,
    getAgreementsState,
    isRootLevel,
    setIsRootLevel,
    agreementsState,
  } = useFileExplorerStore();

  useEffect(() => {
    const typeParam = searchParams.get('t');
    const uuidParam = searchParams.get('id');
    setIsRootLevel(typeParam === 'agree' && uuidParam === null);
  }, [searchParams]);

  const { getFilterByIds, pagination } = useFiltersStore();
  const isMobile = useIsMobile();

  // Local state
  const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<NormalizedAgreement | null>(
    null,
  );
  const [openFilterColumn, setOpenFilterColumn] = useState<string | null>(null);

  const [isExporting, setIsExporting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Event handlers
  const handleNavigate = (uuid: string, type: ExplorerItemType) => {
    onNavigate?.({
      uuid,
      entityType: type,
    });
  };

  const handleEditAgreement = async (values: AgreementDTO) => {
    const updatedAgreement = await updateAgreement(values.uuid, values);
    setSelectedRow(
      (prev) => ({ ...prev, ...updatedAgreement }) as NormalizedAgreement,
    );
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await handleExportExcel(columns);
    } catch (error) {
      console.error('Error exporting Excel:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Column configurations
  const getRegularColumns = (): ColumnDef<NormalizedAgreement>[] => {
    return getColumnsConfig(entityType)
      .filter((column) => visibleColumns.includes(column.accessorKey as string))
      .map((column) => ({
        ...column,
        header: ({
          column: tableColumn,
        }: {
          column: Column<NormalizedAgreement>;
        }) => (
          <ColumnHeaderAgreement
            column={tableColumn}
            currentPage={'dashboard'}
            openFilterColumn={openFilterColumn}
            setOpenFilterColumn={setOpenFilterColumn}
          />
        ),
      })) as ColumnDef<NormalizedAgreement>[];
  };

  const selectionColumn: ColumnDef<NormalizedAgreement> | null = useMemo(() => {
    if (isSmall) return null;
    return {
      id: 'select',
      header: ({ table }) => (
        <div className="flex items-center">
          {table.getIsSomeRowsSelected() ? (
            <CheckboxSome
              checked={table.getIsSomeRowsSelected()}
              onCheckedChange={(value) =>
                table.toggleAllPageRowsSelected(!!value)
              }
              aria-label="Select all"
            />
          ) : (
            <Checkbox
              checked={table.getIsAllPageRowsSelected()}
              onCheckedChange={(value) =>
                table.toggleAllPageRowsSelected(!!value)
              }
              aria-label="Select all"
            />
          )}
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        </div>
      ),
      size: 28,
      enableSorting: false,
      enableResizing: false,
    } as ColumnDef<NormalizedAgreement>;
  }, [isSmall]);

  const columns = isSmall
    ? getSmallColumns<NormalizedAgreement>(
        entityType,
        isMoveFile,
        fileUuidToMove,
        updateFileAfterMove,
        setFileUuidToMove,
        setIsMoveFileDialogOpen,
        setCurrentUUID,
        setCurrentEntityType,
        handleNavigate,
      )
    : ([
        selectionColumn!,
        ...getRegularColumns(),
        getActionsColumn({
          setIsDetailsPanelOpen,
          setSelectedRow,
        }),
      ].filter(Boolean) as ColumnDef<NormalizedAgreement>[]);

  const agreementItemsFetch = useCallback(async () => {
    await getAgreementsState({
      parent_uuid: parentUuid,
      exclude_archived: isRootLevel,
    });
    return Promise.resolve();
  }, [getAgreementsState, parentUuid, isRootLevel]);

  const agreementItems = useMemo(() => {
    return Object.values(agreementsState.entities);
  }, [agreementsState.entities]);

  useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    agreementItemsFetch().then(() => {
      if (!isActive) return;
      setIsLoading(false);
    });
    return () => {
      isActive = false;
    };
  }, [
    getFilterByIds('dashboard') && isRootLevel
      ? JSON.stringify(getFilterByIds('dashboard'))
      : null,
    isRootLevel,
    sorting && isRootLevel ? JSON.stringify(sorting) : null,
    pagination && isRootLevel ? JSON.stringify(pagination) : null,
  ]);

  const table = useReactTable({
    data: agreementItems,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualSorting: true,
    manualPagination: true,
    onSortingChange: setSorting,
    state: {
      sorting,
    },
    getRowId: (row) => row.uuid,
    filterFns: {
      exact: (row, columnId, value): boolean => {
        let cellValue = '';
        if (columnId === 'step') {
          cellValue =
            (useGlobalStore.getState().steps ?? [])
              .find((step) => step.uuid === row.original.step)
              ?.status_name.toLowerCase() || '';
        } else {
          cellValue = String(row.getValue(columnId) || '').toLowerCase();
        }

        const filterValue = String(value).toLowerCase();
        return filterValue === '' || cellValue === filterValue;
      },
    },
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    autoResetAll: false,
  });

  return (
    <>
      <div
        className={cn(
          `relative box-border flex h-full w-full flex-1 flex-col gap-1 overflow-x-hidden rounded-md bg-white transition-all duration-300 ease-in-out`,
          isDetailsPanelOpen ? 'sm:mr-[574px] sm:w-[calc(100%-574px)]' : '',
          isSmall ? '' : 'shadow-sm',
        )}
      >
        {table.getSelectedRowModel().rows.length === 0 ? (
          <TableActionsComponent
            isSmall={isSmall}
            visibleColumns={visibleColumns}
            setVisibleColumns={setVisibleColumns}
            handleExport={handleExport}
            isLoading={isLoading}
            hasMoreItemsAvailable={
              agreementItems.length === (pagination?.pageSize || rowsPerPage)
            }
            entityType={entityType}
            currentPage={'dashboard'}
          />
        ) : null}
        <BreadCrumbsComponent
          breadcrumbs={breadcrumbs}
          isSmall={isSmall}
          searchTerm={searchTerm}
          setCurrentUUID={setCurrentUUID}
          setCurrentEntityType={setCurrentEntityType}
          handleNavigate={handleNavigate}
          totalDocuments={totalActiveAgreements}
          isRootLevel={isRootLevel}
        />

        {/* Table container with fixed width and horizontal scroll */}
        <div
          className={cn(
            'w-full flex-grow',
            isMobile ? 'overflow-auto' : 'relative h-full overflow-y-auto',
          )}
        >
          {/* Different containers based on mobile vs. desktop */}
          <div
            className={cn(
              isMobile || isSmall
                ? 'w-full overflow-x-auto'
                : 'absolute inset-0 overflow-auto',
            )}
          >
            {!isSmall && <AgreementSelectionBar table={table} />}
            <table
              className={cn(
                'w-full border-separate border-spacing-0',
                isMobile ? 'min-w-[340px]' : 'min-w-[640px] max-w-none',
              )}
            >
              {!isSmall && (
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className={`w-auto border-b px-[20px] text-left font-medium sm:px-4 ${
                            isSmall
                              ? 'py-1 text-sm sm:first:pl-[32px] sm:last:pr-[32px]'
                              : 'py-5 text-base sm:first:pl-[36px] sm:last:pr-[60px]'
                          }`}
                          style={{
                            width:
                              header.column.id === 'name' && columns.length < 4
                                ? '100%'
                                : 'auto',
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
              )}
              <tbody>
                {isLoading ? (
                  <TableRowSkeleton columnCount={columns.length} />
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      className={cn(
                        'cursor-pointer border-b border-gray-200',
                        entityType === ExplorerItemType.DIRECTORY &&
                          'hover:bg-neutral-50',
                        'has_file_errors' in row.original &&
                          (row.original as NormalizedAgreement).has_file_errors &&
                          'bg-[#FFF5F5]',
                      )}
                      onClick={() => {
                        if (
                          isMoveFile &&
                          entityType === ExplorerItemType.AGREEMENT
                        ) {
                          return;
                        }
                        setCurrentUUID(row.original.uuid);
                        setCurrentEntityType(ExplorerItemType.FILE);
                        handleNavigate(
                          row.original.uuid,
                          ExplorerItemType.FILE,
                        );
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={`border-b border-separator px-[20px] py-3 sm:px-4 ${
                            isSmall
                              ? 'h-[60px] sm:first:pl-[32px] sm:last:pr-[32px]'
                              : 'h-[60px] sm:h-[70px] sm:first:pl-[36px] sm:last:pr-[60px]'
                          }`}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        {isExporting && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <Loader className="h-4 w-4 animate-spin-slow" />
              <span>Експортування...</span>
            </div>
          </div>
        )}
      </div>
      {selectedRow !== null && (
        <DetailsPanel
          isOpen={isDetailsPanelOpen}
          onClose={() => {
            setIsDetailsPanelOpen(false);
            setSelectedRow(null);
          }}
          data={selectedRow}
          onEdit={handleEditAgreement}
          directoryAbbreviation={
            agreementItems.find((item) => item.uuid === selectedRow.uuid)
              ?.directory_name || ''
          }
        />
      )}
    </>
  );
};
